const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface StoredEmailCodeRequest {
  email: string;
  codeId: string;
  expiresAt: string;
  retryAt: string;
}

export type EmailCodeEntry =
  | { mode: 'manual' }
  | { mode: 'link'; codeId: string; code: string; valid: boolean };

export interface EmailCodeDomainError {
  code?: string;
  details?: {
    attemptsLeft?: number;
    retryAt?: string;
    retryAfterSeconds?: number;
  };
}

export interface EmailCodeRequestStore {
  save(request: StoredEmailCodeRequest): void;
  read(): StoredEmailCodeRequest | null;
  clear(): void;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isValidTimestamp = (value: unknown): value is string =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value));

export const isValidEmailAddress = (email: string): boolean => {
  const normalized = email.trim();
  return normalized.length <= 320 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
};

export const normalizeEmailCode = (value: string) => value.replace(/\D/g, '').slice(0, 6);

export const isValidEmailCode = (value: string) => /^\d{6}$/.test(value);

export const parseStoredEmailCodeRequest = (stored: string | null): StoredEmailCodeRequest | null => {
  if (!stored) return null;

  try {
    const parsed: unknown = JSON.parse(stored);
    if (
      !isRecord(parsed) ||
      typeof parsed.email !== 'string' ||
      typeof parsed.codeId !== 'string' ||
      !isValidTimestamp(parsed.expiresAt) ||
      !isValidTimestamp(parsed.retryAt) ||
      !isValidEmailAddress(parsed.email) ||
      !UUID_PATTERN.test(parsed.codeId)
    ) {
      return null;
    }

    return {
      email: parsed.email,
      codeId: parsed.codeId,
      expiresAt: parsed.expiresAt,
      retryAt: parsed.retryAt,
    };
  } catch {
    return null;
  }
};

export const parseEmailCodeSearch = (search: string): EmailCodeEntry => {
  const params = new URLSearchParams(search);
  const codeId = params.get('codeId');
  const code = params.get('code');
  if (codeId === null && code === null) return { mode: 'manual' };

  return {
    mode: 'link',
    codeId: codeId ?? '',
    code: code ?? '',
    valid: UUID_PATTERN.test(codeId ?? '') && isValidEmailCode(code ?? ''),
  };
};

let stagedEmailCodeEntry: EmailCodeEntry | null = null;

export const stageEmailCodeEntry = (entry: EmailCodeEntry): void => {
  stagedEmailCodeEntry = entry;
};

export const consumeStagedEmailCodeEntry = (): EmailCodeEntry | null => {
  const entry = stagedEmailCodeEntry;
  stagedEmailCodeEntry = null;
  return entry;
};

export const readAndClearEmailCodeEntry = (): EmailCodeEntry => {
  if (typeof window === 'undefined') return consumeStagedEmailCodeEntry() ?? { mode: 'manual' };

  const entry = parseEmailCodeSearch(window.location.search);
  if (entry.mode === 'manual') return consumeStagedEmailCodeEntry() ?? entry;

  window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash}`);
  return entry;
};

export const extractEmailCodeDomainError = (error: unknown): EmailCodeDomainError => {
  if (!isRecord(error) || !isRecord(error.data)) return {};

  const { code, details } = error.data;
  if (typeof code !== 'string') return {};
  if (!isRecord(details)) return { code };

  const domainDetails: EmailCodeDomainError['details'] = {};
  if (typeof details.attemptsLeft === 'number') domainDetails.attemptsLeft = details.attemptsLeft;
  if (typeof details.retryAt === 'string') domainDetails.retryAt = details.retryAt;
  if (typeof details.retryAfterSeconds === 'number') domainDetails.retryAfterSeconds = details.retryAfterSeconds;

  return { code, details: domainDetails };
};

export const createEmailCodeRequestStore = (storageKey: string): EmailCodeRequestStore => ({
  save(request) {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(storageKey, JSON.stringify(request));
  },
  read() {
    if (typeof window === 'undefined') return null;
    const request = parseStoredEmailCodeRequest(window.sessionStorage.getItem(storageKey));
    if (!request) window.sessionStorage.removeItem(storageKey);
    return request;
  },
  clear() {
    if (typeof window !== 'undefined') window.sessionStorage.removeItem(storageKey);
  },
});
